import react, { useState } from "react";
import {
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";
import './GaficaYBarrasDeZona.scss'
import Card from "../../../../../components/Atomsv2/Card/Card";
import Text from "../../../../../components/Atomsv2/Text/Text";
import ProgressBar from "../../../../../components/Atomsv2/ProgressBar/ProgressBar";
import Table from "../../../../../components/moleculesv2/Table/Table";
import Column from "../../../../../components/moleculesv2/Table/Column";
import ModalClienteDetalle from "./ModalClienteDetalle";
import Skeleton from "../../../../../components/Atomsv2/Skeleton/Skeleton";

const GraficaYBarrasDeZonav2 = ({ data, fullData, loading, filtros, zonasOperativas }) => {
    const [clienteDetalle, setClienteDetalle] = useState([]);

    const abrirDetalle = (rowData) => {
        const registrosCliente = fullData?.filter(item => item.ID_CLIENTE === rowData.ID_CLIENTE) ?? [];
        setClienteDetalle(registrosCliente);
    };

    const processChartData = (rawData) => {
        if (!rawData?.length) return [];

        // Contar cuántos registros tiene cada semana
        const conteoSemanas = rawData.reduce((acc, item) => {
            acc[item.SEMANA] = (acc[item.SEMANA] || 0) + 1;
            return acc;
        }, {});

        const semanas = {};

        rawData.forEach(item => {

            if (!semanas[item.SEMANA]) {
                semanas[item.SEMANA] = {
                    semana: item.SEMANA,
                    fechaInicio: item.FECHA_INICIO_SEMANA,
                    fechaFin: item.FECHA_FIN_SEMANA,
                    incidentesOperativos: 0,
                    _eficienciaSum: 0,
                    _eficienciaCount: 0,
                    facturacionReal: 0,
                    serviciosEjecutadosTotal: 0,
                };
            }

            semanas[item.SEMANA].incidentesOperativos += item.INCIDENTES_OPERATIVOS;

            // Promedio de eficiencia por semana — se omiten los valores null
            if (item.EFICIENCIA_OPERATIVA_PCT != null) {
                semanas[item.SEMANA]._eficienciaSum   += item.EFICIENCIA_OPERATIVA_PCT;
                semanas[item.SEMANA]._eficienciaCount += 1;
            }

            semanas[item.SEMANA].facturacionReal += item.FACTURACION_REAL;
            semanas[item.SEMANA].serviciosEjecutadosTotal += item.SERVICIOS_EJECUTADOS_TOTAL;

        });

        // Redondear la eficiencia a 2 decimales
        return Object.values(semanas)
            .map(({ _eficienciaSum, _eficienciaCount, ...item }) => ({
                ...item,
                eficienciaOperativa: Number(
                    (_eficienciaCount > 0 ? _eficienciaSum / _eficienciaCount : 0).toFixed(2)
                ),
                // formato de moneda
                facturacionReal: Number(item.facturacionReal.toFixed(2)),
            }))
            .sort((a, b) => a.semana - b.semana);
    };




    const eficienciaPorZona = zonasOperativas?.map(zona => {
        const registrosZona = data?.filter(item => item.ZONA_OPERATIVA === zona.NOMBRE) ?? [];
        const registrosValidos = registrosZona.filter(item => item.EFICIENCIA_OPERATIVA_PCT != null);
        const promedioEficiencia = registrosValidos.length > 0
            ? registrosValidos.reduce((acc, item) => acc + item.EFICIENCIA_OPERATIVA_PCT, 0) / registrosValidos.length
            : 0;

        return {
            zona,
            promedioEficiencia: Number(promedioEficiencia.toFixed(2)),
        };
    });

    const formatoMoneda = (rowDara) => {
        return rowDara.FACTURACION_REAL.toLocaleString("es-MX", {
            style: "currency",
            currency: "MXN",
        });
    }
    const chartData = processChartData(data);

    return (
        <div className="GaficaYBarrasDeZona">
            <div className="grafica-grid ">
                <div className="graficas">
                    <Card>
                        {
                            loading ? (
                                <Skeleton width="100%" height={300} />
                            ) : (
                                <ResponsiveContainer width="100%" height={300}>
                                    <LineChart
                                        data={chartData}
                                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />

                                        <XAxis
                                            dataKey="semana"
                                            label={{ value: "Semana", position: "insideBottomRight", offset: -5 }}
                                        />

                                        <YAxis
                                            domain={
                                                filtros.metrica.value !== "eficiencia" ? ['dataMin - 5', 'dataMax + 5'] : ['dataMin - 1', 'dataMax + 1']}

                                            label={{ value: "Servicios", angle: -90, position: "insideLeft" }}
                                        />

                                        <Tooltip
                                            labelFormatter={(label) => {
                                                const entry = chartData.find(d => d.semana === label);
                                                return entry
                                                    ? `Semana ${label}: ${entry.fechaInicio} — ${entry.fechaFin}`
                                                    : `Semana ${label}`;
                                            }}
                                            formatter={(value, name) => {
                                                if (name === "Facturación Real") {
                                                    return [
                                                        value.toLocaleString("es-MX", {
                                                            style: "currency",
                                                            currency: "MXN",
                                                        }),
                                                        name,
                                                    ];
                                                }
                                                return [value, name];
                                            }}
                                        />

                                        <Legend />

                                        {filtros.metrica.value === "incidencia" && (
                                            <Line
                                                type="monotone"
                                                dataKey="incidentesOperativos"
                                                stroke="#cc9a3e"
                                                strokeWidth={3}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 7 }}
                                                name="Incidentes Operativos"
                                            />
                                        )}

                                        {filtros.metrica.value === "eficiencia" && (
                                            <Line
                                                type="monotone"
                                                dataKey="eficienciaOperativa"
                                                stroke="#82ca9d"
                                                strokeWidth={3}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 7 }}
                                                name="Eficiencia Operativa"
                                            />
                                        )}

                                        {filtros.metrica.value === "facturacion" && (
                                            <Line
                                                type="monotone"
                                                dataKey="facturacionReal"
                                                stroke="#8884d8"
                                                strokeWidth={3}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 7 }}
                                                name="Facturación Real"
                                            />
                                        )}

                                        {filtros.metrica.value === "servicio" && (
                                            <Line
                                                type="monotone"
                                                dataKey="serviciosEjecutadosTotal"
                                                stroke="#ca8282"
                                                strokeWidth={3}
                                                dot={{ r: 4 }}
                                                activeDot={{ r: 7 }}
                                                name="Servicios Ejecutados"
                                            />
                                        )}
                                    </LineChart>
                                </ResponsiveContainer>
                            )
                        }

                    </Card>
                </div>
                <div className="barras">
                    <Card  >
                        {
                            loading ? (
                                <Skeleton width="100%" height={300} />
                            ) :
                                <>
                                    <Text weight={"600"} size='--font-size-medium'>Nivel de servicio</Text>
                                    <div className="progressContainer">
                                        {eficienciaPorZona.map((zona) => {
                                            const esMenor = eficienciaPorZona.every(z => z.promedioEficiencia >= zona.promedioEficiencia);
                                            return (
                                                <>
                                                    {
                                                        zona.promedioEficiencia !== 0 &&
                                                        <>
                                                            <div
                                                                key={zona.zona.ID}
                                                                className={`zonaMenor`}
                                                            >
                                                                <ProgressBar
                                                                    color={esMenor ? 'var(--red-600)' : undefined}
                                                                    label={`Zona ${zona.zona.NOMBRE}`}
                                                                    value={zona.promedioEficiencia}
                                                                />
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: 4 }}>
                                                                    <div>
                                                                        <Text size="extra-small" weight={'500'} color={'var(--gray-800)'}>Lider: {zona.zona.USUARIO_LIDER}</Text>
                                                                        <Text size="extra-small" weight={'500'} color={'var(--gray-800)'}>Ejecutivo: {zona.zona.USUARIO_EJECUTIVO}</Text>
                                                                    </div>
                                                                    <div style={{ textAlign: 'right' }}>
                                                                        <Text size="extra-small" color={'var(--gray-800)'}>{filtros.periodo.name}</Text>
                                                                        {/* <Text size="extra-small" weight={'700'} color={'var(--blue-600)'}>{promedioZonaPorPeriodo[zona.zona.NOMBRE] ?? '—'}%</Text> */}
                                                                    </div>
                                                                </div>


                                                            </div>
                                                        </>
                                                    }
                                                </>
                                            );
                                        })}
                                    </div>
                                </>
                        }

                    </Card >
                </div>
            </div>
            {/*      
    "ZONA_OPERATIVA": "A",
    "ID_CLIENTE": 134,
    "CLIENTE": "FRESENIUS MEDICAL",
    "AÑO": 2026,
    "SEMANA": 11,
    "FECHA_INICIO_SEMANA": "09/03/2026",
    "FECHA_FIN_SEMANA": "15/03/2026",
    "EJECUTADOS_SUMA": 366,
    "EJECUTADOS_ALIADOS": 0,
    > "SERVICIOS_EJECUTADOS_TOTAL": 366,
    "SUBCONTRATACION_PCT": 0,
    "SERVICIOS_CANCELADOS": 0,
    "SERVICIOS_NO_EJECUTADOS": 0,
    "SERVICIOS_SOLICITADOS_TOTAL": 366,
    "SERVICIOS_PROGRAMADOS_COBRABLES": 366,
    "FACTURACION_ESTIMADA": 303950,
    > "FACTURACION_REAL": 303950,
    "DIFERENCIA_FACTURACION": 0,
    "CUMPLIMIENTO_FACTURACION_PCT": 100,
    "PAGO_SUBCONTRATADO": 0,
    "INGRESO_DESPUES_SUBCONTRATACION": 303950,
    > "INCIDENTES_OPERATIVOS": 4,
    "INCIDENTES_ADMINISTRATIVOS": 1,
    "INCIDENTES_TOTAL": 5,
    > "EFICIENCIA_OPERATIVA_PCT": 98.91
   */}
 
            <Card>
                <Table
                    showCounter
                    skeleton={loading}
                    data={data}
                    globalFilter
                    download
                    height={'calc(100vh - 200px)'}
                    columnSelect={["ZONA_OPERATIVA", "CLIENTE", "SEMANA", "FECHA_INICIO_SEMANA", "FECHA_FIN_SEMANA", "SERVICIOS_EJECUTADOS_TOTAL", "SUBCONTRATACION_PCT", "INCIDENTES_OPERATIVOS", "INCIDENTES_ADMINISTRATIVOS", "INCIDENTES_TOTAL", "EFICIENCIA_OPERATIVA_PCT"]}
                >
                    <Column sortable onClick={abrirDetalle} header='ZONA' value='ZONA_OPERATIVA' />
                    <Column sortable onClick={abrirDetalle} header='ID CLIENTE' value='ID_CLIENTE' />
                    <Column sortable onClick={abrirDetalle} header='CLIENTE' value='CLIENTE' />
                    <Column sortable onClick={abrirDetalle} header='AÑO' value='AÑO' />
                    <Column sortable onClick={abrirDetalle} header='SEMANA' value='SEMANA' />
                    <Column sortable onClick={abrirDetalle} header='INICIO' value='FECHA_INICIO_SEMANA' />
                    <Column sortable onClick={abrirDetalle} header='FIN' value='FECHA_FIN_SEMANA' />
                    <Column sortable onClick={abrirDetalle} header='EJ. SUMA'    value='EJECUTADOS_SUMA'              tooltipHeader='EJECUTADOS SUMA' />
                    <Column sortable onClick={abrirDetalle} header='EJ. ALIADOS' value='EJECUTADOS_ALIADOS'            tooltipHeader='EJECUTADOS DE ALIADOS' />
                    <Column sortable onClick={abrirDetalle} header='SERV. EJEC.' value='SERVICIOS_EJECUTADOS_TOTAL'    tooltipHeader='SERVICIOS EJECUTADOS TOTALES' />
                    <Column sortable onClick={abrirDetalle} header='SUBCONT. %'  value='SUBCONTRATACION_PCT'           tooltipHeader='SUBCONTRATACIÓN %' />

                    <Column sortable onClick={abrirDetalle} header='SOLIC.'      value='SERVICIOS_SOLICITADOS_TOTAL'   tooltipHeader='SERVICIOS SOLICITADOS TOTALES' />
                    <Column sortable onClick={abrirDetalle} header='PROG. COB.'  value='SERVICIOS_PROGRAMADOS_COBRABLES' tooltipHeader='SERVICIOS PROGRAMADOS COBRABLES' />
                    <Column sortable onClick={abrirDetalle} header='CANCEL.'     value='SERVICIOS_CANCELADOS'          tooltipHeader='SERVICIOS CANCELADOS' />
                    <Column sortable onClick={abrirDetalle} header='NO EJEC.'    value='SERVICIOS_NO_EJECUTADOS'       tooltipHeader='SERVICIOS NO EJECUTADOS' />
                    <Column sortable onClick={abrirDetalle} header='FACT. EST.'  value='FACTURACION_ESTIMADA'          body={formatoMoneda} tooltipHeader='FACTURACIÓN ESTIMADA' />
                    <Column sortable onClick={abrirDetalle} header='FACT. REAL'  value='FACTURACION_REAL'              body={formatoMoneda} tooltipHeader='FACTURACIÓN REAL' />
                    <Column sortable onClick={abrirDetalle} header='DIF. FACT.'  value='DIFERENCIA_FACTURACION'        body={formatoMoneda} tooltipHeader='DIFERENCIAS DE FACTURACIÓN' />
                    <Column sortable onClick={abrirDetalle} header='CUMPL. %'    value='CUMPLIMIENTO_FACTURACION_PCT'  body={formatoMoneda} tooltipHeader='CUMPLIMIENTO DE FACTURACIÓN' />
                    <Column sortable onClick={abrirDetalle} header='PAGO SC.'    value='PAGO_SUBCONTRATADO'            body={formatoMoneda} tooltipHeader='PAGO SUB CONTRATADO' />
                    <Column sortable onClick={abrirDetalle} header='ING. SC.'    value='INGRESO_DESPUES_SUBCONTRATACION' body={formatoMoneda} tooltipHeader='INGRESO DESPUÉS DE SUB CONTRATACIÓN' />

                    <Column sortable onClick={abrirDetalle} header='INC. OPER.'  value='INCIDENTES_OPERATIVOS'         tooltipHeader='INCIDENTES OPERATIVOS' />
                    <Column sortable onClick={abrirDetalle} header='INC. ADM.'   value='INCIDENTES_ADMINISTRATIVOS'    tooltipHeader='INCIDENTES ADMINISTRATIVOS' />
                    <Column sortable onClick={abrirDetalle} header='INC. TOTAL'  value='INCIDENTES_TOTAL'              tooltipHeader='INCIDENTES TOTAL' />
                    <Column sortable onClick={abrirDetalle} header='EFIC. %'     value='EFICIENCIA_OPERATIVA_PCT'      tooltipHeader='EFICIENCIA OPERATIVA %' />
                </Table>
            </Card>
            {clienteDetalle.length > 0 && (
                <ModalClienteDetalle data={clienteDetalle} setData={setClienteDetalle} />
            )}
        </div>
    )
}
export default GraficaYBarrasDeZonav2